import { useState, useCallback, useRef } from 'react';
import { getAttackSound } from '../sounds/attackSoundMapping';
import { playSoundEffect } from './useSoundEffects';

/**
 * Hook to manage attack animations
 * Handles registration, animation state, and completion of attack animations
 * @param {WebSocket} socket - WebSocket connection
 * @param {string} gameId - Game ID
 * @param {boolean} isSpectating - Whether the user is spectating (should not send animation-complete)
 * @param {number} animationSpeedMultiplier - Speed multiplier for animations (1 = normal, 0.5 = 2x slower)
 */
export const useAttackAnimations = (socket, gameId = null, isSpectating = false, animationSpeedMultiplier = 1) => {
  const [pendingAttacks, setPendingAttacks] = useState(new Map());
  const [activeAnimations, setActiveAnimations] = useState(new Map());
  const [hiddenUnits, setHiddenUnits] = useState(new Set()); // Units hidden during animation
  const animationQueueRef = useRef([]);
  const processingRef = useRef(false);
  const completeAnimationRef = useRef(null);
  const processAnimationQueueRef = useRef(null);
  const completedAttacksRef = useRef(new Set()); // ✅ FIX: Track completed attacks to prevent duplicate completions
  // ✅ FIX: Persistent tracking of attackers that died - survives even after attack is removed from pendingAttacks
  const deadAttackersRef = useRef(new Set()); // Track instanceIds of attackers that died
  // ✅ FIX: Persistent tracking of targets that died - survives even after attack is removed from pendingAttacks
  const deadTargetsRef = useRef(new Set()); // Track instanceIds of targets that died
  
  // Store animation speed multiplier in ref to access in timeouts
  const speedMultiplierRef = useRef(animationSpeedMultiplier);
  speedMultiplierRef.current = animationSpeedMultiplier;

  /**
   * Start animation for a specific attack
   * @param {string} attackId - Attack ID to animate
   */
  const startAnimation = useCallback((attackId) => {
    setPendingAttacks(prev => {
      const pendingAttack = prev.get(attackId);
      if (!pendingAttack) {
        // ✅ FIX: Check if attack was already completed (expected case)
        if (completedAttacksRef.current.has(attackId)) {
          // Attack was already completed - this is expected, just skip silently
          processingRef.current = false;
          // Process next in queue
          if (animationQueueRef.current.length > 0 && processAnimationQueueRef.current) {
            const nextId = animationQueueRef.current.shift();
            setTimeout(() => processAnimationQueueRef.current(), 0);
          }
          return prev;
        }
        // Attack not found and not completed - might be a timing issue, log debug
        console.log(`ℹ️ Attack ${attackId} not found in pending attacks (may have been removed)`);
        processingRef.current = false;
        // Process next in queue
        if (animationQueueRef.current.length > 0 && processAnimationQueueRef.current) {
          const nextId = animationQueueRef.current.shift();
          setTimeout(() => processAnimationQueueRef.current(), 0);
        }
        return prev;
      }
      
      console.log(`🎬 Starting animation for attack: ${attackId}`);
      setActiveAnimations(prevAnim => {
        const next = new Map(prevAnim);
        next.set(attackId, {
          status: 'animating',
          startedAt: Date.now()
        });
        return next;
      });
      
      return prev;
    });
  }, []);

  /**
   * Process the animation queue sequentially
   */
  const processAnimationQueue = useCallback(() => {
    if (processingRef.current || animationQueueRef.current.length === 0) {
      return;
    }
    
    processingRef.current = true;
    const attackId = animationQueueRef.current.shift();
    startAnimation(attackId);
  }, [startAnimation]);
  
  // Store in ref for access before initialization
  processAnimationQueueRef.current = processAnimationQueue;

  /**
   * Register a new attack that needs animation
   * @param {string} attackId - Unique attack ID
   * @param {Object} outcome - Attack outcome from backend
   * @param {boolean} isBotAttack - Whether this is a bot attack (auto-complete after animation)
   */
  const registerAttack = useCallback((attackId, outcome, isBotAttack = false) => {
    console.log(`🎬 Registering attack animation: ${attackId}`, { isBotAttack, outcome });
    
    // ✅ CRITICAL: Prevent duplicate registrations (especially after counterstrike pauses)
    // Check if attack was already registered using a ref to avoid stale closures
    if (pendingAttacks.has(attackId)) {
      console.warn(`⚠️ Attack ${attackId} was already registered - skipping duplicate registration`);
      return; // Don't register if already present
    }
    
    // ✅ Play attack sound based on attacker's abilities and attack_sound_type
    if (outcome.attacker) {
      const attacker = outcome.attacker;
      
      // Helper function to check if unit has an ability (case-insensitive)
      const hasAbility = (unit, ability) => {
        const abilityLower = ability.toLowerCase();
        const checkArray = (arr) => {
          if (!Array.isArray(arr)) return false;
          return arr.some(ab => ab && ab.toLowerCase() === abilityLower);
        };
        
        if (unit.hasInstanceModifications && unit.instanceAbilities) {
          return checkArray(unit.instanceAbilities);
        }
        return checkArray(unit.abilities);
      };
      
      // Check for attack sound - priority: attack_sound_type from database > ability overrides
      let attackSoundPath = null;
      
      // ✅ CRITICAL FIX: Prioritize attack_sound_type from database over ability-based overrides
      // This allows units like 329 (Ironclad Tank) with Penetrate to play their correct sound (cannon)
      // instead of being forced to play penetrate.mp3
      if (attacker.attack_sound_type && attacker.attack_sound_type !== 'default') {
        // Use database attack_sound_type if available (highest priority)
        attackSoundPath = getAttackSound(attacker);
        console.log(`🔊 Using database attack_sound_type="${attacker.attack_sound_type}" for ${attacker.name || 'attacker'} (ID: ${attacker.id})`);
        console.log(`🔊 Playing attack sound: ${attackSoundPath} for ${attacker.name || 'attacker'}`);
      } else if (hasAbility(attacker, 'Penetrate')) {
        // Units with Penetrate play penetrate.mp3 only if no attack_sound_type is set
        attackSoundPath = '/sounds/penetrate.mp3';
        console.log(`🔊 Playing penetrate sound for ${attacker.name || 'attacker'} (has Penetrate ability, no attack_sound_type)`);
      } else if (hasAbility(attacker, 'Fell')) {
        // Units with Fell play fell.mp3 only if no attack_sound_type is set
        attackSoundPath = '/sounds/fell.mp3';
        console.log(`🔊 Playing fell sound for ${attacker.name || 'attacker'} (has Fell ability, no attack_sound_type)`);
      } else {
        // Default: use attack_sound_type (may fall back to default/blunt)
        attackSoundPath = getAttackSound(attacker);
        console.log(`🔊 Attack sound debug:`, {
          attackerName: attacker.name,
          attackerId: attacker.id,
          attack_sound_type: attacker.attack_sound_type,
          hasAttackSoundType: !!attacker.attack_sound_type
        });
        console.log(`🔊 Playing attack sound: ${attackSoundPath} for ${attacker.name || 'attacker'}`);
      }
      
      // Play the attack sound
      if (attackSoundPath) {
        playSoundEffect(attackSoundPath, 0.6); // Play at 60% volume
      }
      
      // Units with Recover play heal.mp3 in addition to their attack sound
      if (hasAbility(attacker, 'Recover')) {
        console.log(`🔊 Playing heal sound for ${attacker.name || 'attacker'} (has Recover ability)`);
        playSoundEffect('/sounds/heal.mp3', 0.6); // Play at 60% volume
      }
    } else {
      console.warn(`⚠️ No attacker in outcome, cannot play attack sound`);
    }
    
    // ✅ FIX: Clear completed status when registering new attack (allows re-registration if needed)
    completedAttacksRef.current.delete(attackId);
    
    // ✅ FIX: Track attackers that will die - store in persistent ref BEFORE attack is removed
    // This survives even after the attack is removed from pendingAttacks
    if (outcome.attackerDestroyed && outcome.attackerInstanceId) {
      deadAttackersRef.current.add(outcome.attackerInstanceId);
      console.log(`💀 Tracked dead attacker: ${outcome.attackerInstanceId} - will suppress death animations`);
    }
    
    // ✅ BUG FIX: Track targets as dead when attack is registered to suppress death animations
    // This prevents duplicate damage animations when the target dies
    // Note: We still update this in attack-complete to handle protection effects correctly,
    // but we need to suppress death animations immediately to prevent the second animation
    if (outcome.targetDestroyed && outcome.targetInstanceId) {
      deadTargetsRef.current.add(outcome.targetInstanceId);
      console.log(`💀 Tracked dead target: ${outcome.targetInstanceId} - will suppress death animations`);
    }
    
    setPendingAttacks(prev => {
      const next = new Map(prev);
      next.set(attackId, {
        outcome,
        registeredAt: Date.now(),
        isBotAttack
      });
      return next;
    });
    
    // Hide the attacker unit during animation
    if (outcome.attackerInstanceId) {
      setHiddenUnits(prev => new Set([...prev, outcome.attackerInstanceId]));
    }
    
    // Queue animation
    animationQueueRef.current.push(attackId);
    if (processAnimationQueueRef.current) {
      processAnimationQueueRef.current();
    }
    
    // ✅ BOT ANIMATION FIX: Auto-complete bot attacks after animation duration
    // Bot attacks should auto-complete so damage is applied without waiting for user interaction
    // ✅ SPEED CONTROL: Scale timeout by animation speed multiplier
    const speedFactor = speedMultiplierRef.current > 0 ? speedMultiplierRef.current : 1;
    const botTimeout = Math.round(1500 / speedFactor); // 1.5s at 100% speed
    const safetyTimeout = Math.round(5000 / speedFactor); // 5s at 100% speed
    
    if (isBotAttack) {
      setTimeout(() => {
        setPendingAttacks(prev => {
          if (prev.has(attackId) && completeAnimationRef.current) {
            console.log(`🤖 Bot attack ${attackId} animation complete - auto-completing (timeout: ${botTimeout}ms)`);
            completeAnimationRef.current(attackId);
          }
          return prev;
        });
      }, botTimeout);
    } else {
      // ✅ SAFETY: Auto-complete animation after timeout if it doesn't complete
      // This prevents attacks from getting stuck if animations fail
      setTimeout(() => {
        setPendingAttacks(prev => {
          if (prev.has(attackId) && completeAnimationRef.current) {
            console.warn(`⚠️ Attack ${attackId} animation timeout - auto-completing (timeout: ${safetyTimeout}ms)`);
            completeAnimationRef.current(attackId);
          }
          return prev;
        });
      }, safetyTimeout);
    }
  }, []);


  /**
   * Complete animation and send confirmation to backend
   * @param {string} attackId - Attack ID that completed
   */
  const completeAnimation = useCallback((attackId) => {
    // ✅ FIX: Prevent duplicate completions using ref (synchronous check)
    if (completedAttacksRef.current.has(attackId)) {
      console.warn(`⚠️ Duplicate completion attempt for attack ${attackId} - ignoring`);
      return; // Already completed, don't process again
    }
    
    console.log(`🎬 Completing animation for attack: ${attackId}`);
    
    // Mark as completed immediately to prevent race conditions
    completedAttacksRef.current.add(attackId);
    
    // Get pending attack from state before deleting
    setPendingAttacks(prev => {
      const pendingAttack = prev.get(attackId);
      
      // ✅ FIX: Only send animation-complete if attack is still pending
      // This prevents sending completion for attacks that were already processed
      if (!pendingAttack) {
        console.warn(`⚠️ Cannot complete animation for attack ${attackId}: attack not found in pending attacks`);
        console.warn(`⚠️ This might be a duplicate completion or the attack was already processed`);
        // Remove from completed set since we didn't actually process it
        completedAttacksRef.current.delete(attackId);
        return prev; // Don't send message or modify state
      }
      
      // Send animation-complete message to backend
      // ✅ SPECTATOR FIX: Spectators should NEVER send animation-complete messages
      // The actual players in the game will send it, and bot attacks auto-complete
      if (isSpectating) {
        // Spectator watching a game - don't send animation-complete (they're not a player)
        console.log(`👁️ Skipping animation-complete for ${attackId} (spectator mode - not a player)`);
      } else if (socket && socket.readyState === WebSocket.OPEN) {
        try {
          const message = JSON.stringify({
            type: 'animation-complete',
            attackId: attackId,
            gameId: gameId // ✅ FIX: Include gameId for reliable game lookup
          });
          socket.send(message);
          console.log(`✅ Sent animation-complete for attack: ${attackId}, gameId: ${gameId}`);
        } catch (error) {
          // ✅ ELECTRON FIX: Catch errors when sending in Electron (socket.send() might fail silently)
          console.error(`❌ Failed to send animation-complete for ${attackId} in Electron:`, error);
          console.warn(`⚠️ Backend timeout (5s) will auto-apply this attack if message wasn't received`);
        }
      } else if (!socket) {
        // No socket available - this is unexpected for players, but expected for spectators
        console.log(`👁️ Skipping animation-complete for ${attackId} (no socket)`);
      } else {
        console.warn(`⚠️ Socket not ready to send animation-complete (readyState: ${socket.readyState})`);
        console.warn(`⚠️ Backend timeout (5s) will auto-apply this attack if socket doesn't reconnect`);
      }
      
      // ✅ CRITICAL FIX: Show hidden units after a short delay (in case they're still animating)
      // Note: We use the outcome from pendingAttack which has the PRE-CALCULATED destruction status
      // The CORRECTED outcome will come in attack-complete message, so we'll handle it there
      // ✅ ELECTRON FIX: Also set a fallback timeout to show units if attack-complete never arrives
      if (pendingAttack && pendingAttack.outcome.attackerInstanceId) {
        setTimeout(() => {
          setHiddenUnits(prev => {
            const next = new Set(prev);
            // Only show if attacker survived (based on pre-calculated outcome)
            // This will be corrected when attack-complete arrives with actual destruction status
            if (!pendingAttack.outcome.attackerDestroyed) {
              next.delete(pendingAttack.outcome.attackerInstanceId);
            }
            return next;
          });
        }, 100);
        
        // ✅ ELECTRON FIX: Fallback timeout to show units if attack-complete never arrives (6 seconds)
        // This ensures units don't stay hidden forever if the backend timeout fires but frontend doesn't receive attack-complete
        setTimeout(() => {
          setHiddenUnits(prev => {
            if (prev.has(pendingAttack.outcome.attackerInstanceId)) {
              console.warn(`⚠️ Fallback: Showing unit ${pendingAttack.outcome.attackerInstanceId} after 6s timeout (attack-complete may not have arrived)`);
              const next = new Set(prev);
              next.delete(pendingAttack.outcome.attackerInstanceId);
              return next;
            }
            return prev;
          });
        }, 6000); // 6 seconds (backend timeout is 5s, so this gives 1s buffer)
      }
      
      // Delete from pending attacks
      const next = new Map(prev);
      next.delete(attackId);
      return next;
    });
    
    // Clean up animation state
    setActiveAnimations(prev => {
      const next = new Map(prev);
      next.delete(attackId);
      return next;
    });
    
    // Process next animation in queue
    processingRef.current = false;
    processAnimationQueue();
  }, [socket, gameId, isSpectating, processAnimationQueue]);
  
  // Store completeAnimation in ref for timeout access
  completeAnimationRef.current = completeAnimation;

  /**
   * Check if a unit should be hidden during animation
   * @param {string} instanceId - Unit instance ID
   * @returns {boolean} - True if unit should be hidden
   */
  const isUnitHidden = useCallback((instanceId) => {
    return hiddenUnits.has(instanceId);
  }, [hiddenUnits]);

  /**
   * Get pending attack by ID
   * @param {string} attackId - Attack ID
   * @returns {Object|null} - Pending attack or null
   */
  const getPendingAttack = useCallback((attackId) => {
    return pendingAttacks.get(attackId) || null;
  }, [pendingAttacks]);

  /**
   * Get active animation by ID
   * @param {string} attackId - Attack ID
   * @returns {Object|null} - Active animation or null
   */
  const getActiveAnimation = useCallback((attackId) => {
    return activeAnimations.get(attackId) || null;
  }, [activeAnimations]);

  /**
   * Check if a unit is a target in any pending attack
   * Used to suppress automatic damage animations for attack targets
   * @param {string} instanceId - Unit instance ID
   * @returns {boolean} - True if unit is a target in a pending attack
   */
  const isUnitTargetInPendingAttack = useCallback((instanceId) => {
    if (!instanceId) return false;
    for (const [attackId, pendingAttack] of pendingAttacks.entries()) {
      if (pendingAttack.outcome?.targetInstanceId === instanceId) {
        return true;
      }
    }
    return false;
  }, [pendingAttacks]);

  /**
   * Check if a unit is an attacker that died during an attack
   * Uses persistent ref that survives even after attack is removed from pendingAttacks
   * @param {string} instanceId - Unit instance ID
   * @returns {boolean} - True if unit is an attacker that died (suppress death animations)
   */
  const isUnitAttackerInPendingAttack = useCallback((instanceId) => {
    if (!instanceId) return false;
    // ✅ FIX: Check persistent ref first - this survives after attack is removed
    if (deadAttackersRef.current.has(instanceId)) {
      return true;
    }
    // Also check pending attacks (for attacks still in progress)
    for (const [attackId, pendingAttack] of pendingAttacks.entries()) {
      if (pendingAttack.outcome?.attackerInstanceId === instanceId) {
        return true;
      }
    }
    return false;
  }, [pendingAttacks]);
  
  /**
   * Clean up dead attacker tracking after a delay (prevents memory leak)
   * Called after attack-complete message arrives
   */
  const cleanupDeadAttacker = useCallback((instanceId) => {
    if (instanceId) {
      // Clean up after 5 seconds (enough time for slot death detection to run)
      setTimeout(() => {
        deadAttackersRef.current.delete(instanceId);
        console.log(`🧹 Cleaned up dead attacker tracking: ${instanceId}`);
      }, 5000);
    }
  }, []);

  const cleanupDeadTarget = useCallback((instanceId) => {
    if (instanceId) {
      // Clean up after 5 seconds (enough time for slot death detection to run)
      setTimeout(() => {
        deadTargetsRef.current.delete(instanceId);
        console.log(`🧹 Cleaned up dead target tracking: ${instanceId}`);
      }, 5000);
    }
  }, []);

  /**
   * Reset all animation state - call when game ends to prevent ghost animations
   */
  const reset = useCallback(() => {
    console.log('🧹 [useAttackAnimations] Resetting all animation state');
    
    // Clear all pending attacks
    setPendingAttacks(new Map());
    
    // Clear all active animations
    setActiveAnimations(new Map());
    
    // Clear hidden units
    setHiddenUnits(new Set());
    
    // Clear animation queue
    animationQueueRef.current = [];
    
    // Reset processing flag
    processingRef.current = false;
    
    // Clear completed attacks tracking
    completedAttacksRef.current.clear();
    
    // Clear dead attacker/target tracking
    deadAttackersRef.current.clear();
    deadTargetsRef.current.clear();
  }, []);

  return {
    registerAttack,
    completeAnimation,
    isUnitHidden,
    getPendingAttack,
    getActiveAnimation,
    isUnitTargetInPendingAttack,
    isUnitAttackerInPendingAttack,
    cleanupDeadAttacker,
    cleanupDeadTarget,
    reset, // ✅ FIX: Expose reset function to clear all animation state on game end
    deadAttackersRef, // ✅ FIX: Expose ref directly for synchronous checking
    deadTargetsRef, // ✅ FIX: Expose ref directly for synchronous checking
    setHiddenUnits, // ✅ CRITICAL FIX: Expose setHiddenUnits so BattleUI can update it when attack-complete arrives
    pendingAttacks: Array.from(pendingAttacks.entries()),
    activeAnimations: Array.from(activeAnimations.entries())
  };
};

